import { useState, useMemo } from "react"
import { useLocalization } from "@tm/localization"
import { errorHandling } from "@tm/utils"
import { MenuItem, Icon, SelectTextField, Stack, TextField, ToggleSwitch, IconButton, PercentageField } from "@tm/components"
import { Controller, useFormContext } from "react-hook-form"
import { useCustomRepairTimesDefaults } from "@tm/data/basket"
import { ValueLabelStack } from "../../../../../ValueLabelStack"
import { BasketWork } from "../../../../../../models"
import { FixedPriceFields } from "./FixedPriceFields"
import { HourlyRateFields } from "./HourlyRateFields"
import { useRepairTimesFormDefaults } from "../../../../../../hooks/Forms/useRepairTimesFormDefaults"

type Props = {
    hasFixedPrice: boolean
    work?: BasketWork
    repairTimeDivision: number
    showRepairTimesInHours: boolean
    workNumberRequired: boolean
    onChangeHasFixedPrice(value: boolean): void
}

export default function WorkDialogFields(props: Props) {
    const { work, showRepairTimesInHours, hasFixedPrice, repairTimeDivision, workNumberRequired, onChangeHasFixedPrice } = props
    const { translateText, formatNumber } = useLocalization()
    const { control, watch } = useFormContext()
    const { vatRateItems, workCategoryItems } = useRepairTimesFormDefaults()
    const { customRepairTimesDefaults: { defaultCurrencyCode } = {} } = useCustomRepairTimesDefaults()

    const [originalFieldsExpanded, setOriginalFieldsExpanded] = useState<boolean>(true)

    const repairTimesTextId = showRepairTimesInHours ? 84 : 1550

    const surcharge = Number(watch("surcharge") ?? 0)
    const hourlyRate = Number(watch("hourlyRate") ?? 0)
    const time = Number(watch("time") ?? 0)
    const rebate = watch("rebate")
    const fixedPrice = watch("fixedPriceValue")

    const calculatedPrice = useMemo(() => {
        if (hasFixedPrice) {
            return (fixedPrice || 0) - (fixedPrice || 0) * ((rebate || 0) / 100)
        }
        const rate = (hourlyRate ?? 0) + (hourlyRate ?? 0) * ((surcharge || 0) / 100)
        const price = (rate * (time || 0)) / (repairTimeDivision > 0 ? repairTimeDivision : 1)
        const newPrice = price - price * ((rebate || 0) / 100)
        return newPrice
    }, [hourlyRate, repairTimeDivision, surcharge, time, rebate, hasFixedPrice, fixedPrice])

    return (
        <Stack spacing={1}>
            <Stack direction="row" gap={1}>
                <Controller
                    control={control}
                    name="workNumber"
                    render={({ field, fieldState }) => (
                        <TextField
                            label={translateText(58)}
                            size="extralarge"
                            required={workNumberRequired}
                            sx={{ width: "235px" }}
                            {...field}
                            {...errorHandling(fieldState)}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="description"
                    render={({ field, fieldState }) => (
                        <TextField
                            required
                            label={translateText(25)}
                            size="extralarge"
                            sx={{ width: "435px" }}
                            {...field}
                            {...errorHandling(fieldState)}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="category"
                    render={({ field, fieldState }) => (
                        <SelectTextField
                            label={translateText(158)}
                            size="extralarge"
                            maxLayerHeight={300}
                            sx={{ width: "380px" }}
                            {...field}
                            {...errorHandling(fieldState)}
                        >
                            {workCategoryItems?.map((cat) => (
                                <MenuItem key={cat.category} value={cat.category}>
                                    {cat.text}
                                </MenuItem>
                            )) || []}
                        </SelectTextField>
                    )}
                />
            </Stack>
            <Stack direction="row" gap={1} alignItems="center">
                {originalFieldsExpanded && (
                    <>
                        <ToggleSwitch
                            size="small"
                            right={{ value: translateText(246), key: true }}
                            left={{ value: translateText(63), key: true }}
                            selected={hasFixedPrice}
                            onChange={() => onChangeHasFixedPrice(!hasFixedPrice)}
                        />
                        {hasFixedPrice ? (
                            <FixedPriceFields repairTimesTextId={repairTimesTextId} defaultCurrencyCode={defaultCurrencyCode} />
                        ) : (
                            <HourlyRateFields defaultCurrencyCode={defaultCurrencyCode} repairTimeDivision={repairTimeDivision} />
                        )}
                    </>
                )}
                {work && (
                    <IconButton onClick={() => setOriginalFieldsExpanded(!originalFieldsExpanded)} size="large">
                        <Icon name={originalFieldsExpanded ? "arrow-right" : "arrow-left"} />
                    </IconButton>
                )}
                <Controller
                    control={control}
                    name="rebate"
                    render={({ field, fieldState }) => (
                        <PercentageField
                            label={`${translateText(54)} %`}
                            size="extralarge"
                            sx={{ width: "105px" }}
                            {...field}
                            {...errorHandling(fieldState)}
                        />
                    )}
                />
                <ValueLabelStack label={`${translateText(57)} ${defaultCurrencyCode}`} value={formatNumber(calculatedPrice, 2)} />
                <Controller
                    control={control}
                    name="vatType"
                    render={({ field, fieldState }) => (
                        <SelectTextField
                            label={translateText(706)}
                            size="extralarge"
                            sx={{ width: "250px" }}
                            {...field}
                            {...errorHandling(fieldState)}
                        >
                            {vatRateItems.map(({ vatRate, text }) => (
                                <MenuItem key={vatRate.vatType} value={vatRate.vatType}>
                                    {text}
                                </MenuItem>
                            ))}
                        </SelectTextField>
                    )}
                />
            </Stack>
        </Stack>
    )
}
